import { useContext } from "react";
import { useInAppWindowFocusOptional } from "@/lib/inAppWindowFocus";
import { DocumentEditorContext } from "@/pages/dokumente/documentEditorContextValue";
import { ProjectOverviewContext } from "@/projectOverview/projectOverviewContextValue";
import {
  PROJECT_OVERVIEW_NESTED_OVERLAY_Z,
  PROJECT_OVERVIEW_OVERLAY_Z,
} from "@/projectOverview/projectOverviewLayers";

/**
 * Z-Index-Stack für Stundennachweis / Fertigstellung.
 *
 * Standard:
 *   flow 230 → overlay 240 → nested 250 → deep 260 → deeper 270
 *
 * Über Projektübersicht / Dokumenteneditor (z-280/310), nur wenn der
 * Stundennachweis zuletzt fokussiert wurde (z. B. aus dem Rechnungsentwurf):
 *   flow 320 → overlay 330 → nested 340 → deep 350 → deeper 360
 *
 * Öffnet man den Rechnungsentwurf aus dem Stundennachweis, bleibt der Flow
 * auf dem Standard-Stack – der Editor (z-280) liegt dann oben.
 */
export interface TimesheetFlowLayers {
  /** Haupt-Dialog (Editor / Fertigstellung / Detail). */
  flow: string;
  /** Erste Ebene darüber (Zeitzeile, Material, Finalize, Unknown-Confirm, Actions). */
  overlay: string;
  /** Zweite Ebene (Actions-Follow-ups, Billing answered, …). */
  nested: string;
  /** Dritte Ebene (Mail, Invoice choice, Billing answer). */
  deep: string;
  /** Vierte Ebene (Multi-select, Material über Billing). */
  deeper: string;
  /** Suggestion-Picker / PDF über dem jeweiligen Parent – mind. overlay. */
  picker: string;
}

const DEFAULT_LAYERS: TimesheetFlowLayers = {
  flow: "z-[230]",
  overlay: "z-[240]",
  nested: "z-[250]",
  deep: "z-[260]",
  deeper: "z-[270]",
  picker: "z-[250]",
};

/** Über Dokumenteneditor (280) und Projektübersicht/Dokumentenkette (310). */
const OVER_CONTEXT_LAYERS: TimesheetFlowLayers = {
  flow: PROJECT_OVERVIEW_OVERLAY_Z, // z-[320]
  overlay: PROJECT_OVERVIEW_NESTED_OVERLAY_Z, // z-[330]
  nested: "z-[340]",
  deep: "z-[350]",
  deeper: "z-[360]",
  picker: "z-[340]",
};

export function useTimesheetFlowLayers(): TimesheetFlowLayers {
  // Optional: PrintLocationDialog u. a. können außerhalb des Providers
  // gemountet werden – dann Standard-Stack, kein Crash.
  const projectOverviewOpen = useContext(ProjectOverviewContext)?.isOpen ?? false;
  const documentEditorOpen = useContext(DocumentEditorContext)?.isOpen ?? false;
  const frontSurface = useInAppWindowFocusOptional()?.frontSurface ?? null;
  const timesheetIsFront = frontSurface === "timesheet";
  const otherContextOpen = projectOverviewOpen || documentEditorOpen;

  // Nur anheben, wenn der Stundennachweis zuletzt nach vorne geholt wurde.
  // Sonst bleibt z. B. der aus dem Detail geöffnete Rechnungsentwurf oben.
  if (timesheetIsFront && otherContextOpen) {
    return OVER_CONTEXT_LAYERS;
  }
  return DEFAULT_LAYERS;
}
